import { useState } from "react";
import { useDashboard, StatCard } from "../lib/useDashboard";
import WeeklyPlanPanel, { weekLabel } from "./WeeklyPlanPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Users, ClipboardList, FileText, LayoutDashboard, PenLine, Target } from "lucide-react";

export default function StaffDashboard({ userName, onWriteNote, onQuickNote }) {
  const { data, loading, reload } = useDashboard();
  const [openConsumer, setOpenConsumer] = useState(null);
  const consumers = data?.consumers || [];
  const s = data?.stats || {};

  return (
    <div data-testid="staff-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <LayoutDashboard className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">My Consumers</h1>
          <p className="text-sm text-[#7A8C82]">Hi {userName?.split(" ")[0]} — here's who you're working with · {weekLabel(data?.week_start)}</p>
        </div>
      </div>

      {loading && <p className="text-sm text-[#7A8C82]">Loading…</p>}

      {!loading && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <StatCard testid="staff-stat-consumers" icon={Users} label="Assigned to me" value={s.consumers ?? 0} />
            <StatCard testid="staff-stat-plans" icon={ClipboardList} label="Plans ready this week" value={s.plans_ready ?? 0} color="#D4A373" />
            <StatCard testid="staff-stat-notes" icon={FileText} label="My notes this week" value={s.notes_this_week ?? 0} />
          </div>

          {consumers.length === 0 && (
            <div className="bg-white border border-dashed border-[#E2DFD8] rounded-2xl p-10 text-center" data-testid="staff-no-consumers">
              <Users className="w-8 h-8 mx-auto mb-3 text-[#D4A373]" strokeWidth={1.5} />
              <p className="text-sm text-[#3A453F] max-w-sm mx-auto">No consumers are assigned to you yet. Ask your QP to assign them — they'll show up here automatically.</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {consumers.map((c) => (
              <div key={c.id} data-testid={`staff-consumer-card-${c.id}`} className="bg-white border border-[#E2DFD8] rounded-2xl p-5 shadow-sm flex flex-col fade-up">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-extrabold text-[#121814] truncate">{c.name}</p>
                    <p className="text-xs text-[#7A8C82]">{c.member_id || "No ID"}{c.diagnosis ? ` · ${c.diagnosis}` : ""}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-1 rounded-md ${c.has_plan ? "bg-[#EAF1EC] text-[#4A6B53]" : "bg-[#F6E9C9] text-[#8a6d1f]"}`} data-testid={`plan-badge-${c.id}`}>
                    {c.has_plan ? `${c.plan_done ?? 0}/${c.plan_interventions} done` : "No plan yet"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {[["Goals", c.goals_count], ["Notes this wk", c.notes_this_week], ["Last note", c.last_note_date ? c.last_note_date.slice(5) : "—"]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-[#F9F9F6] border border-[#E2DFD8] py-2">
                      <p className="text-[9px] font-bold text-[#7A8C82] uppercase tracking-wider">{l}</p>
                      <p className="text-sm font-semibold text-[#121814]">{v}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`view-plan-btn-${c.id}`} onClick={() => setOpenConsumer(c)}
                    className="flex-1 h-11 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                    <Target className="w-4 h-4" strokeWidth={1.5} /> This week's plan
                  </button>
                  <button data-testid={`quick-note-btn-${c.id}`} onClick={() => onQuickNote(c)}
                    className="h-11 px-4 rounded-full bg-white border border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white text-sm font-medium flex items-center gap-2 transition-colors">
                    <PenLine className="w-4 h-4" strokeWidth={1.5} /> Note
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!openConsumer} onOpenChange={(o) => !o && setOpenConsumer(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="staff-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{openConsumer?.name}</DialogTitle>
            <DialogDescription>This week's planned interventions. Tap the ones you did, then write your note.</DialogDescription>
          </DialogHeader>
          {openConsumer && (
            <WeeklyPlanPanel consumer={openConsumer} canGenerate={false} onChanged={reload}
              onWriteNote={(c, goals, interventions) => { setOpenConsumer(null); onWriteNote(c, goals, interventions); }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
